import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  GetTransactionsQueryDto,
  DateString,
  Sort,
  AutoTxId,
  ImportId,
} from "../../api/types";
import { applyFilters, type FiltersWithPeriod } from "../../logic/filter-logic";
import { isPeriodId, resolvePeriod, type PeriodId } from "../../logic/period";

export const useTransactionSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<FiltersWithPeriod>(() => {
    const periodRaw = searchParams.get("period");
    const period: PeriodId | null = isPeriodId(periodRaw) ? periodRaw : null;

    // When a period is set, it owns the date range — explicit start/end in the
    // URL are ignored. Resolution to concrete dates happens here so downstream
    // consumers (TanStack Query, the API call) see a normal date range.
    let startDate: DateString | null;
    let endDate: DateString | null;
    if (period) {
      const resolved = resolvePeriod(period);
      startDate = resolved.startDate;
      endDate = resolved.endDate;
    } else {
      startDate = searchParams.get("startDate") as DateString | null;
      endDate = searchParams.get("endDate") as DateString | null;
    }

    const sourceFilter = searchParams.getAll("source");
    const expenseTypeFilter = searchParams.getAll("expenseType");
    const groupFilter = searchParams.getAll("group");
    const categoryFilter = searchParams.getAll("category");
    const merchant = searchParams.get("merchant");
    const description = searchParams.get("description");
    const autoTransactionId = searchParams.getAll("autoTransactionId");
    const importId = searchParams.get("importId");
    const sort = (searchParams.get("sort") as Sort) || "dateDesc";
    const limitParam = searchParams.get("limit");
    const limit = limitParam ? parseInt(limitParam, 10) || 10 : 10;
    const ignored = searchParams.get("ignored") === "true";
    const matchProblem = searchParams.get("matchProblem") === "true";

    return {
      period,
      startDate,
      endDate,
      source: sourceFilter.length > 0 ? sourceFilter : null,
      expenseType: expenseTypeFilter.length > 0 ? expenseTypeFilter : null,
      group: groupFilter.length > 0 ? groupFilter : null,
      category: categoryFilter.length > 0 ? categoryFilter : null,
      merchant: merchant || null,
      description: description || null,
      autoTransactionId:
        autoTransactionId.length > 0 ? (autoTransactionId as AutoTxId[]) : null,
      importId: importId ? (importId as ImportId) : null,
      ignored: ignored ? true : null,
      matchProblem: matchProblem ? true : null,
      limit,
      cursor: null,
      sort,
    };
  }, [searchParams]);

  const setFilters = (
    newFilters: Partial<GetTransactionsQueryDto> & {
      period?: PeriodId | null;
    },
  ) => {
    setSearchParams((prev) => {
      const nextParams = applyFilters(prev, { ...filters, ...newFilters });

      if (newFilters.sort !== undefined) {
        if (newFilters.sort) {
          nextParams.set("sort", newFilters.sort);
        } else {
          nextParams.delete("sort");
        }
      }

      if (newFilters.limit !== undefined) {
        if (newFilters.limit) {
          nextParams.set("limit", newFilters.limit.toString());
        } else {
          nextParams.delete("limit");
        }
      }

      return nextParams;
    });
  };

  return { filters, setFilters };
};
